// Angular modules.
import {
	AfterViewInit,
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChildren,
} from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular-ivy';
import { ZkEButtonColor, ZkEButtonSize } from '@zenklub/ng-ui';
import { ApiErrorResponse } from 'app/interfaces/error.interface';
import { IZkuProfessional } from 'app/interfaces/professionalprofile.interface';
import { AuthenticationService } from 'app/services/authentication.service';
import { ChatService } from 'app/services/chat.service';
import { DateTimeService } from 'app/services/datetime.service';
import { ErrorService } from 'app/services/error.service';
import { GlobalService } from 'app/services/global.service';
import { HotjarService } from 'app/services/hotjar.service';
import { IdWallService } from 'app/services/id-wall.service';
import { NetworkService } from 'app/services/network.service';
import { StompService } from 'app/services/stomp.service';
import { SubscriptionService } from 'app/services/subscription.service';
import { UiService } from 'app/services/ui-service/ui-service.service';
import { UserService } from 'app/services/user.service';
import { WePayOutService } from 'app/services/wepayout.service';
import { ZkuService } from 'app/services/zku.service';
import { customMessage } from 'app/shared/validations/custom-message';
import { enterAnimation, loadingAnimation } from 'app/utils/animations.util';
import { environment } from 'environments/environment';
import firebase from 'firebase';
import { Observable } from 'rxjs';
import { getBucket } from 'secchiojs';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	animations: [enterAnimation, loadingAnimation],
})
export class LoginComponent implements OnInit, AfterViewInit {
	@Output() testX: EventEmitter<string> = new EventEmitter<string>();
	@ViewChildren('inputEmail') emailField: {
		first: { nativeElement: { focus: () => void } };
	};
	@ViewChildren('inputPassword') passField: {
		first: { nativeElement: { focus: () => void } };
	};
	loading = false;
	starting = true;
	justSignedUp: boolean;
	error = '';
	// Status.
	currentOp = 'Teste';
	statusLoader: string;
	userIsLoggedIn = false;
	model: any = {};
	email = '';
	password = '';
	state = '';
	paddingTop: number;
	// Translated strings.
	strPreparing: string;
	strStarting: string;
	strStatusTrying: string;
	strStatusChat: string;
	strStatusCheck: string;
	strStatusData: string;
	strStatusStarting: string;
	strStatusUpdating: string;

	strPasswordValidation: string;
	strEmailValidation: string;
	strPasswordIsRequired: string;

	redirectUrl: string;

	setEmail: (email: string) => void;

	form: FormGroup;
	buttonColorType = ZkEButtonColor;
	buttonSizeType = ZkEButtonSize;

	activeLogin = false;

	isMobile$: Observable<boolean>;

	// Constructor.
	constructor(
		private activatedRoute: ActivatedRoute,
		private afAuth: AngularFireAuth,
		private formBuilder: FormBuilder,
		private hotjarService: HotjarService,
		private uiService: UiService,
		private zkuService: ZkuService,
		protected authenticationService: AuthenticationService,
		protected chatService: ChatService,
		protected dateTime: DateTimeService,
		protected errorService: ErrorService,
		protected global: GlobalService,
		protected idWallService: IdWallService,
		protected network: NetworkService,
		protected router: Router,
		protected stompService: StompService,
		protected subscriptionService: SubscriptionService,
		protected translate: TranslateService,
		protected userService: UserService,
		protected wePayOutService: WePayOutService
	) {
		this.isMobile$ = this.uiService.onDetectLargeDevices();

		// override the route reuse strategy
		this.router.routeReuseStrategy.shouldReuseRoute = function () {
			return false;
		};
		this.router.events.subscribe((evt) => {
			if (evt instanceof NavigationEnd) {
				// trick the Router into believing it's last link wasn't previously loaded
				this.router.navigated = false;
			}
		});

		const [setEmail] = getBucket('zkmail', {
			defaultValue: null,
			version: 7,
		});
		this.setEmail = setEmail;

		this.redirectUrl = this.activatedRoute.snapshot.queryParams['redirectUrl'];
	}

	// On init.
	public ngOnInit() {
		this.onInitForm();
		this.onToggleForm();

		// Get translated texts.
		this.translate
			.get([
				'VALIDATIONS_FORM.PASSWORD',
				'VALIDATIONS_FORM.EMAIL_REQUIRED',
				'VALIDATIONS_FORM.PASSWORD_REQUIRED',
				'GENERAL.PREPARING',
				'GENERAL.STARTING',
				'LOGIN.STATUS.CHAT',
				'LOGIN.STATUS.CHECKDATA',
				'LOGIN.STATUS.TRYING',
				'LOGIN.STATUS.GETDATA',
				'LOGIN.STATUS.STARTINGAPP',
				'LOGIN.STATUS.UPDATELOCALDATA',
			])
			.subscribe((res) => {
				// Strings.
				this.strPreparing = res['GENERAL.PREPARING'];
				this.strStarting = res['GENERAL.STARTING'];
				this.strStatusChat = res['LOGIN.STATUS.CHAT'];
				this.strStatusCheck = res['LOGIN.STATUS.CHECKDATA'];
				this.strStatusTrying = res['LOGIN.STATUS.TRYING'];
				this.strStatusData = res['LOGIN.STATUS.GETDATA'];
				this.strStatusStarting = res['LOGIN.STATUS.STARTINGAPP'];
				this.strStatusUpdating = res['LOGIN.STATUS.UPDATELOCALDATA'];

				// Loader status.
				this.statusLoader = this.strStarting;

				//password form
				this.strPasswordValidation = res['VALIDATIONS_FORM.PASSWORD'];
				this.strPasswordIsRequired = res['VALIDATIONS_FORM.PASSWORD_REQUIRED'];
				this.strEmailValidation = res['VALIDATIONS_FORM.EMAIL_REQUIRED'];

				this.onInitForm();
			});

		this.afAuth.onAuthStateChanged((user: firebase.User) => {
			if (user && !this.loading) {
				let zku = this.zkuService.getZku() || ({} as IZkuProfessional);
				const userData = {
					id: zku.id || user.uid,
					name: zku.name || user.displayName,
					email: zku.email || user.email,
					imageUrl: zku.imageUrl || user.photoURL,
				};
				zku = Object.assign({}, zku, userData);
				this.zkuService.setZku(zku);
				this.redirectToApp();
			}
		});

		// Loader status.
		this.statusLoader = this.strPreparing || 'Zenklub Office';

		// Continue.
		this.statusLoader = this.strStarting;
	}

	// After view init.
	public ngAfterViewInit() {
		this.state = 'in';
	}

	onInitForm(): void {
		this.form = this.formBuilder.group({
			email: [
				'',
				[
					customMessage(Validators.required, this.strEmailValidation),
					Validators.email,
				],
			],
			password: [
				'',
				[
					customMessage(Validators.required, this.strPasswordIsRequired),
					customMessage(Validators.minLength(6), this.strPasswordValidation),
				],
			],
		});
	}

	onActiveLogin(): void {
		this.activeLogin = !this.activeLogin;
	}

	onToggleForm(): void {
		if (this.activatedRoute.snapshot.queryParams['accountCreated'] === 'true') {
			this.justSignedUp = true;
		} else {
			this.onActiveLogin();
		}
	}

	// Try login.
	login() {
		// Show loader.
		this.loading = true;
		// Current status.
		this.currentOp = this.strStatusTrying;

		const { email, password } = this.form.value;
		const emailLowerCase: string = email && email.toLocaleLowerCase();

		//check blacklist
		this.authenticationService.checkBlacklist(emailLowerCase).subscribe(
			() => {
				// Try to log in.
				this.afAuth
					.signInWithEmailAndPassword(emailLowerCase, password)
					.then((result) => {
						return result.user.getIdTokenResult();
					})
					.then((tokenData) => {
						// if it is not a professional reject it
						if (!tokenData || !tokenData.claims.professional) {
							throw { type: 'HTTP.NO_ACCESS' };
						}

						// Current status.
						this.currentOp = this.strStatusData;
						const uid = firebase.auth().currentUser.uid;
						// Store token, uid and last used e-mail.
						this.setEmail(emailLowerCase);

						Sentry.configureScope((scope: any) => {
							scope.setUser({
								id: uid,
								email: emailLowerCase,
							});
						});

						// Get user's data.
						this.subscriptionService
							.getSubscriptionList()
							.subscribe((subsData) => {
								const isPremium = subsData.subscriptions.some(
									(subscription) => subscription.status === 'active'
								);

								this.userService
									.getProfessionalProfileData(uid)
									.subscribe((userData) => {
										// subscription stuff
										this.subscriptionService.addToDataLayer(subsData);

										if (userData) {
											const userImageUrl =
												userData.imageUrl ||
												this.global.getDefaultUserPicture();

											this.zkuService.setZku({
												...userData,
												especialty: userData.mainType,
												imageUrl: userImageUrl,
												name: userData.displayName,
												isPremium,
												profile: userData.profileUrl,
												timezone: userData.timezone
													? userData.timezone
													: this.dateTime.getTimezoneName(),
											});

											// Current status.
											this.currentOp = this.strStatusUpdating;
											// Update local data.
											this.network.updateLocalData().then(
												() => {
													// Current status.
													this.currentOp = this.strStatusChat;
													// Current status.
													this.currentOp = this.strStatusStarting;
													// Redirect to application.
													this.redirectToApp();
												},
												() => {
													throw { type: 'HTTP.UPDATELOCALDATA' };
												}
											);
										} else {
											// throw error
											throw new ApiErrorResponse({
												error: {
													code: 'AUTH/INVALID-CREDENTIALS',
												},
											});
										}
									});
							});
					})
					.catch((error: any) => {
						const aerror = new ApiErrorResponse({ error });
						if (aerror.isApiError() || aerror.isFirebaseError()) {
							this.error = this.errorService.getMessage(aerror.error);
						} else if (error.type) {
							this.error = this.errorService.getMessage(error.type);
						}

						this.userService.setOnlineStatus(false).subscribe(() => {
							this.authenticationService.logoutServices();
						});

						this.loading = false;
					});
			},
			(error: ApiErrorResponse) => {
				this.error = this.errorService.getMessage(error.error);
				this.loading = false;
			}
		);
	}

	// Redirect to app.
	protected async redirectToApp() {
		// Loaded.
		const userDt = this.userService.getUserLocal();

		if (!userDt) {
			return;
		}

		// subscribe for events updates
		this.wePayOutService.responseWePayOutValidation(userDt.id);
		this.idWallService.responseCnpjValidation(userDt.id);
		this.stompService.connectToAppointmentsTopic(userDt.id);
		// we just logged in, we need to init chat,
		// since the chat init code on app.component.ts has already run and
		// was ignored because we were not logged in
		await this.chatService.initChat();
		if (this.chatService.browserNotificationsIs('granted')) {
			await this.chatService.createInbox();
		}
		// Get user's data.
		this.currentOp = this.strStatusCheck;

		if (this.redirectUrl) {
			if (this.redirectUrl.substr(0, 4) === 'http') {
				window.location.href = this.redirectUrl;
			} else {
				const url = new URL(`${window.location.origin}${this.redirectUrl}`);
				const queryParams =
					url.search.length &&
					JSON.parse(
						'{"' +
							decodeURI(url.search.substr(1))
								.replace(/"/g, '\\"')
								.replace(/&/g, '","')
								.replace(/=/g, '":"') +
							'"}'
					);
				this.router.navigate([url.pathname], { queryParams });
			}
		} else if (
			userDt &&
			(!userDt.timezone || userDt.timezone.indexOf('undefined') > -1)
		) {
			this.router.navigateByUrl('/account/definitions');
		} else {
			// Redirect to home page.
			this.router.navigateByUrl('/calendar');
		}
	}

	onForgotPassword(): void {
		window.location.href =
			environment.websiteurl + 'forgot-password?office=true';
	}

	onCreateAccount(): void {
		window.open(environment.websiteurl + 'sou-profissional/', '_blank');
	}
}
